import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { ACTIVE_LANGS, LANG_PREFIX } from '../i18n/config';
import { useCart } from '../contexts/CartContext';
import { trackEvent } from '../analytics';
import { SHOW_CONFIGURATOR_LINKS } from '../config/features';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { lang, t, path } = useLanguage();
  const { totalItems } = useCart();

  const cartLink = (onNavigate) => (
    <Link
      to={path('/cart')}
      onClick={onNavigate}
      aria-label={t('cart.title')}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', color: location.pathname === path('/cart') ? '#dfa926' : '#d1d5db', textDecoration: 'none' }}
    >
      <ShoppingCart size={20} />
      {totalItems > 0 && (
        <span style={{
          position: 'absolute', top: '-0.5rem', right: '-0.6rem',
          backgroundColor: '#dfa926', color: 'black',
          fontSize: '0.65rem', fontWeight: 700,
          minWidth: '1rem', height: '1rem', borderRadius: '0.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 0.2rem'
        }}>
          {totalItems}
        </span>
      )}
    </Link>
  );

  const navItems = [
    { name: t('nav.home'), path: "/" },
    { name: t('nav.products'), path: "/products" },
    { name: t('nav.about'), path: "/about" },
    { name: t('nav.services'), path: "/services" },
    { name: t('nav.quality'), path: "/quality" },
    { name: t('nav.downloads'), path: "/downloads" },
    { name: t('nav.contact'), path: "/contact" },
  ];

  // Door configurator — highlighted in gold as the main way to order.
  const configuratorPath = path('/configurator');
  const configuratorActive = location.pathname.replace(/\/$/, '') === configuratorPath.replace(/\/$/, '');
  const configuratorLink = (onNavigate, mobile) => (
    <Link
      to={configuratorPath}
      onClick={() => {
        trackEvent('configurator_click', { location: mobile ? 'nav_mobile' : 'nav' });
        if (onNavigate) onNavigate();
      }}
      style={{
        display: mobile ? 'inline-block' : 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        fontSize: '0.875rem',
        fontWeight: 700,
        textDecoration: 'none',
        color: configuratorActive ? 'black' : '#dfa926',
        backgroundColor: configuratorActive ? '#dfa926' : 'transparent',
        border: '1px solid #dfa926',
        borderRadius: '999px',
        padding: '0.3rem 0.85rem',
        margin: mobile ? '0.5rem 0' : 0,
        transition: 'background-color 0.2s, color 0.2s'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dfa926'; e.currentTarget.style.color = 'black'; }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = configuratorActive ? '#dfa926' : 'transparent';
        e.currentTarget.style.color = configuratorActive ? 'black' : '#dfa926';
      }}
    >
      {t('nav.configurator')}
    </Link>
  );

  // The same page in another language (strips any active lang prefix,
  // then adds the target one). Drives off ACTIVE_LANGS so a future
  // language appears automatically once it is published.
  const strippedPath = location.pathname.replace(/^\/(et|da)(\/|$)/, '/');
  const langPath = (l) => `${LANG_PREFIX[l]}${strippedPath === '/' ? '' : strippedPath}` || '/';

  const langSwitcher = (onNavigate) => (
    <>
      {ACTIVE_LANGS.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span style={{ color: '#4b5563' }}>|</span>}
          <Link
            to={langPath(l)}
            style={langLinkStyle(lang === l)}
            onClick={() => {
              if (l !== lang) trackEvent('language_switch', { from: lang, to: l });
              if (onNavigate) onNavigate();
            }}
          >
            {l.toUpperCase()}
          </Link>
        </React.Fragment>
      ))}
    </>
  );

  const navStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1050,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #3a3a3a'
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1.5rem'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '4rem'
  };


  const desktopMenuStyle = {
    display: 'flex',
    gap: '1.25rem',
    alignItems: 'center'
  };

  const navLinkStyle = (isActive) => ({
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    transition: 'color 0.3s',
    color: isActive ? '#dfa926' : '#d1d5db'
  });

  const langSwitchStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.5rem',
    paddingLeft: '1rem',
    borderLeft: '1px solid #3a3a3a',
    fontSize: '0.8rem',
    fontWeight: '600'
  };

  const langLinkStyle = (isActive) => ({
    textDecoration: 'none',
    transition: 'color 0.3s',
    color: isActive ? '#dfa926' : '#9ca3af',
    fontWeight: isActive ? '700' : '600'
  });

  const mobileButtonStyle = {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer'
  };

  const mobileMenuStyle = {
    paddingTop: '1rem',
    paddingBottom: '1rem',
    borderTop: '1px solid #3a3a3a'
  };

  const mobileLinkStyle = (isActive) => ({
    display: 'block',
    padding: '0.5rem 0',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    transition: 'color 0.3s',
    color: isActive ? '#dfa926' : '#d1d5db'
  });

  return (
    <nav style={navStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <Link to={path('/')} style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src="/logo.png" 
              alt="Hestia Doors Logo"
              style={{ height: '2.4rem', width: 'auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = '<div style="color: #dfa926; font-size: 1.5rem; font-weight: bold; text-decoration: none;">HESTIA DOORS</div>';
              }}
            />
          </Link>

          {/* Desktop Menu */}
          <div style={desktopMenuStyle} className="d-none d-lg-flex">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={path(item.path)}
                style={navLinkStyle(location.pathname === path(item.path))}
                onMouseEnter={(e) => e.target.style.color = '#dfa926'}
                onMouseLeave={(e) => e.target.style.color = location.pathname === path(item.path) ? '#dfa926' : '#d1d5db'}
              >
                {item.name}
              </Link>
            ))}

            {SHOW_CONFIGURATOR_LINKS && configuratorLink()}

            {cartLink()}

            {/* Language Switcher */}
            <div style={langSwitchStyle}>{langSwitcher()}</div>
          </div>

          {/* Mobile Menu Button */}
          <button
            style={mobileButtonStyle}
            className="d-lg-none"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div style={mobileMenuStyle} className="d-lg-none">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={path(item.path)}
                style={mobileLinkStyle(location.pathname === path(item.path))}
                onClick={() => setIsOpen(false)}
                onMouseEnter={(e) => e.target.style.color = '#dfa926'}
                onMouseLeave={(e) => e.target.style.color = location.pathname === path(item.path) ? '#dfa926' : '#d1d5db'}
              >
                {item.name}
              </Link>
            ))}
            {SHOW_CONFIGURATOR_LINKS && configuratorLink(() => setIsOpen(false), true)}
            <div style={{ padding: '0.5rem 0' }}>
              {cartLink(() => setIsOpen(false))}
            </div>
            <div style={{ ...langSwitchStyle, marginLeft: 0, paddingLeft: 0, borderLeft: 'none', paddingTop: '0.75rem', marginTop: '0.5rem', borderTop: '1px solid #3a3a3a' }}>
              {langSwitcher(() => setIsOpen(false))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
